import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Alert, CircularProgress,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Switch, FormControlLabel, IconButton, Divider, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';

const API = process.env.REACT_APP_API_URL || '';

const TIPO_OPCIONES = [
  { value: 'info',    label: 'Información', color: '#1565c0' },
  { value: 'warning', label: 'Aviso',       color: '#e65100' },
  { value: 'success', label: 'Noticia',     color: '#2e7d32' },
  { value: 'error',   label: 'Urgente',     color: '#c62828' },
];

export default function Configuracion() {
  const [avisos, setAvisos] = useState([]);
  const [nuevoAviso, setNuevoAviso] = useState({ texto: '', tipo: 'info' });
  const [guardandoAviso, setGuardandoAviso] = useState(false);
  const [exitoAviso, setExitoAviso] = useState('');

  // Turnero info items
  const [turneroItems, setTurneroItems] = useState([]);
  const [guardandoTurnero, setGuardandoTurnero] = useState(false);
  const [exitoTurnero, setExitoTurnero] = useState('');

  // Seguridad Turnero — PIN
  const [turneroPin, setTurneroPin] = useState(null);
  const [regenerandoPin, setRegenerandoPin] = useState(false);
  const [exitoPin, setExitoPin] = useState('');

  // Políticas Operativas
  const [politicas, setPoliticas] = useState({
    evolucion_obligatoria: false, evolucion_min_caracteres: 30, permitir_borradores: true,
    confirmacion_asistencia_modo: 'deshabilitada', confirmacion_asistencia_horas_antes: 48,
  });
  const [guardandoPoliticas, setGuardandoPoliticas] = useState(false);
  const [exitoPoliticas, setExitoPoliticas] = useState('');

  // Reset datos de prueba
  const [dialogReset, setDialogReset] = useState(false);
  const [confirmTexto, setConfirmTexto] = useState('');
  const [reseteando, setReseteando] = useState(false);
  const [resultadoReset, setResultadoReset] = useState('');

  // Pantalla de inicio
  const [inicio, setInicio] = useState({ nombre_clinica: '', subtitulo: '', logo_base64: '' });
  const [guardandoInicio, setGuardandoInicio] = useState(false);
  const [exitoInicio, setExitoInicio] = useState('');
  const [errorInicio, setErrorInicio] = useState('');
  const logoInputRef = useRef(null);

  useEffect(() => {
    cargarAvisos();
    axios.get(`${API}/api/configuracion/inicio`)
      .then(r => setInicio(r.data))
      .catch(() => {});
    axios.get(`${API}/api/configuracion`)
      .then(r => {
        if (r.data?.turnero_info_items) {
          try { setTurneroItems(JSON.parse(r.data.turnero_info_items)); } catch {}
        }
      })
      .catch(() => {});
    axios.get(`${API}/api/turnero/pin`)
      .then(r => setTurneroPin(r.data))
      .catch(() => {});
    axios.get(`${API}/api/configuracion/politicas`)
      .then(r => setPoliticas(r.data))
      .catch(() => {});
  }, []);

  async function guardarPoliticas() {
    setGuardandoPoliticas(true);
    setExitoPoliticas('');
    try {
      await axios.put(`${API}/api/configuracion/politicas`, politicas);
      setExitoPoliticas('Políticas guardadas.');
      setTimeout(() => setExitoPoliticas(''), 3000);
    } catch (e) {
      setExitoPoliticas('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setGuardandoPoliticas(false);
    }
  }

  function cargarAvisos() {
    axios.get(`${API}/api/avisos/todos`)
      .then(r => setAvisos(r.data))
      .catch(() => {});
  }

  async function crearAviso() {
    if (!nuevoAviso.texto.trim()) return;
    setGuardandoAviso(true);
    try {
      await axios.post(`${API}/api/avisos`, nuevoAviso);
      setNuevoAviso({ texto: '', tipo: 'info' });
      cargarAvisos();
      setExitoAviso('Aviso creado.');
      setTimeout(() => setExitoAviso(''), 3000);
    } catch (e) {
      setExitoAviso('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setGuardandoAviso(false);
    }
  }

  async function toggleAviso(av) {
    await axios.put(`${API}/api/avisos/${av.id}`, { ...av, activo: av.activo ? 0 : 1 });
    cargarAvisos();
  }

  async function eliminarAviso(id) {
    await axios.delete(`${API}/api/avisos/${id}`);
    cargarAvisos();
  }

  async function handleResetear() {
    setReseteando(true);
    setResultadoReset('');
    try {
      await axios.delete(`${API}/api/admin/resetear-datos`);
      setResultadoReset('ok');
    } catch (e) {
      setResultadoReset('error: ' + (e.response?.data?.error || e.message));
    } finally {
      setReseteando(false);
      setDialogReset(false);
      setConfirmTexto('');
    }
  }

  async function regenerarPin() {
    setRegenerandoPin(true);
    setExitoPin('');
    try {
      const r = await axios.post(`${API}/api/turnero/pin/regenerar`);
      setTurneroPin(r.data);
      setExitoPin('PIN regenerado. Actualizá la pantalla del turnero.');
      setTimeout(() => setExitoPin(''), 6000);
    } catch (e) {
      setExitoPin('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setRegenerandoPin(false);
    }
  }

  async function guardarTurnero() {
    setGuardandoTurnero(true);
    setExitoTurnero('');
    try {
      await axios.put(`${API}/api/configuracion/turnero`, { items: turneroItems });
      setExitoTurnero('Información guardada.');
      setTimeout(() => setExitoTurnero(''), 3000);
    } catch (e) {
      setExitoTurnero('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setGuardandoTurnero(false);
    }
  }

  function handleLogoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setErrorInicio('El logo no puede superar 2 MB.'); return; }
    const reader = new FileReader();
    reader.onload = ev => setInicio(i => ({ ...i, logo_base64: ev.target.result }));
    reader.readAsDataURL(file);
  }

  async function guardarInicio() {
    setGuardandoInicio(true);
    setErrorInicio('');
    setExitoInicio('');
    try {
      await axios.put(`${API}/api/configuracion/inicio`, inicio);
      setExitoInicio('Pantalla de inicio guardada.');
      setTimeout(() => setExitoInicio(''), 3000);
    } catch (e) {
      setErrorInicio(e.response?.data?.error || 'Error al guardar.');
    } finally {
      setGuardandoInicio(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 520 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)', mb: 4 }}>
        Configuración
      </Typography>

      {/* ── Avisos / Banner ─────────────────────────────────────── */}

      <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)', mb: 1 }}>
        Banner de avisos
      </Typography>
      <Typography sx={{ color: '#b0b0b0', mb: 3, fontSize: 14 }}>
        Los avisos activos se muestran como ticker desplazante debajo del encabezado para todos los usuarios.
      </Typography>

      {/* Nuevo aviso */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <TextField
          label="Texto del aviso"
          value={nuevoAviso.texto}
          onChange={e => setNuevoAviso(a => ({ ...a, texto: e.target.value }))}
          onKeyDown={e => e.key === 'Enter' && crearAviso()}
          sx={{ flex: 1, minWidth: 220,
            '& .MuiInputBase-root': { color: '#e0e0e0' },
            '& .MuiInputLabel-root': { color: '#90a4ae' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' }
          }}
        />
        <FormControl sx={{ minWidth: 140 }}>
          <InputLabel sx={{ color: '#90a4ae' }}>Tipo</InputLabel>
          <Select
            value={nuevoAviso.tipo}
            label="Tipo"
            onChange={e => setNuevoAviso(a => ({ ...a, tipo: e.target.value }))}
            sx={{ color: '#e0e0e0',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' },
              '& .MuiSvgIcon-root': { color: '#e0e0e0' }
            }}
          >
            {TIPO_OPCIONES.map(t => (
              <MenuItem key={t.value} value={t.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: t.color }} />
                  {t.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={crearAviso}
          disabled={guardandoAviso || !nuevoAviso.texto.trim()}
          sx={{ bgcolor: 'var(--c-primario)', height: 56, '&:hover': { filter: 'brightness(1.2)' } }}
        >
          Agregar
        </Button>
      </Box>

      {exitoAviso && (
        <Alert severity={exitoAviso.startsWith('Error') ? 'error' : 'success'} sx={{ mb: 2 }}>
          {exitoAviso}
        </Alert>
      )}

      {/* Lista de avisos */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {avisos.length === 0 && (
          <Typography sx={{ color: '#666', fontSize: 14 }}>No hay avisos creados.</Typography>
        )}
        {avisos.map(av => {
          const tipo = TIPO_OPCIONES.find(t => t.value === av.tipo) || TIPO_OPCIONES[0];
          return (
            <Box key={av.id} sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              bgcolor: 'var(--c-superficie)', p: 1.5, borderRadius: 2,
              border: `1px solid ${av.activo ? tipo.color : 'rgba(255,255,255,0.08)'}`,
              opacity: av.activo ? 1 : 0.5
            }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: tipo.color, flexShrink: 0 }} />
              <Typography sx={{ flex: 1, color: '#e0e0e0', fontSize: 14, wordBreak: 'break-word' }}>
                {av.texto}
              </Typography>
              <Chip label={tipo.label} size="small" sx={{ bgcolor: tipo.color + '33', color: tipo.color, fontSize: 11 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!av.activo}
                    onChange={() => toggleAviso(av)}
                    size="small"
                    sx={{ '& .MuiSwitch-thumb': { bgcolor: av.activo ? tipo.color : '#555' } }}
                  />
                }
                label={<Typography sx={{ fontSize: 12, color: '#90a4ae' }}>{av.activo ? 'Activo' : 'Inactivo'}</Typography>}
                sx={{ m: 0 }}
              />
              <IconButton onClick={() => eliminarAviso(av.id)} size="small" sx={{ color: '#ef9a9a' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      {/* ── Pantalla de inicio ───────────────────────────────────────── */}
      <Divider sx={{ my: 5, borderColor: 'rgba(255,255,255,0.1)' }} />

      <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)', mb: 1 }}>
        Pantalla de inicio
      </Typography>
      <Typography sx={{ color: '#b0b0b0', mb: 3, fontSize: 14 }}>
        Personalizá el logo, el nombre y el subtítulo que ven los usuarios en la pantalla de login.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Preview del login */}
        <Box sx={{
          bgcolor: 'var(--c-fondo)', border: '1px solid var(--c-primario)',
          borderRadius: 2, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1
        }}>
          <Typography sx={{ color: '#90a4ae', fontSize: 11, mb: 1, alignSelf: 'flex-start' }}>Vista previa del login</Typography>
          {inicio.logo_base64
            ? <img src={inicio.logo_base64} alt="logo" style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 8 }} />
            : <LocalHospitalIcon sx={{ fontSize: 52, color: 'var(--c-acento)' }} />
          }
          <Typography sx={{ color: '#e0e0e0', fontWeight: 700, fontSize: 18 }}>
            {inicio.nombre_clinica || 'Sistema Médico'}
          </Typography>
          <Typography sx={{ color: '#b0b0b0', fontSize: 13 }}>
            {inicio.subtitulo || 'Gestión de turnos e historiales'}
          </Typography>
        </Box>

        {/* Logo */}
        <Box sx={{ bgcolor: 'var(--c-superficie)', p: 2, borderRadius: 2, border: '1px solid var(--c-primario)' }}>
          <Typography sx={{ color: '#e0e0e0', fontWeight: 600, mb: 1 }}>Logo</Typography>
          <Typography sx={{ color: '#90a4ae', fontSize: 12, mb: 1.5 }}>
            PNG o JPG, máximo 2 MB. Se mostrará en la pantalla de ingreso.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {inicio.logo_base64 && (
              <img src={inicio.logo_base64} alt="logo actual" style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)' }} />
            )}
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => logoInputRef.current?.click()}
              sx={{ color: 'var(--c-acento)', borderColor: 'var(--c-primario)', '&:hover': { borderColor: 'var(--c-acento)' } }}
            >
              {inicio.logo_base64 ? 'Cambiar logo' : 'Subir logo'}
            </Button>
            {inicio.logo_base64 && (
              <Button variant="text" size="small" onClick={() => setInicio(i => ({ ...i, logo_base64: '' }))}
                sx={{ color: '#ef9a9a', fontSize: 12, textTransform: 'none' }}>
                Quitar logo
              </Button>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={handleLogoChange}
            />
          </Box>
        </Box>

        {/* Nombre y subtítulo */}
        <TextField
          label="Nombre de la clínica / consultorio"
          value={inicio.nombre_clinica}
          onChange={e => setInicio(i => ({ ...i, nombre_clinica: e.target.value }))}
          placeholder="Sistema Médico"
          inputProps={{ maxLength: 80, style: { color: '#e0e0e0' } }}
          InputLabelProps={{ style: { color: '#90a4ae' } }}
          sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}
        />
        <TextField
          label="Subtítulo"
          value={inicio.subtitulo}
          onChange={e => setInicio(i => ({ ...i, subtitulo: e.target.value }))}
          placeholder="Gestión de turnos e historiales"
          inputProps={{ maxLength: 120, style: { color: '#e0e0e0' } }}
          InputLabelProps={{ style: { color: '#90a4ae' } }}
          sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}
        />
      </Box>

      {exitoInicio && <Alert severity="success" sx={{ mt: 2 }}>{exitoInicio}</Alert>}
      {errorInicio && <Alert severity="error" sx={{ mt: 2 }}>{errorInicio}</Alert>}

      <Button variant="contained" onClick={guardarInicio} disabled={guardandoInicio}
        sx={{ mt: 3, bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}>
        {guardandoInicio ? 'Guardando...' : 'Guardar pantalla de inicio'}
      </Button>

      {/* ── Turnero — info institucional ────────────────────────────── */}
      <Divider sx={{ my: 5, borderColor: 'rgba(255,255,255,0.1)' }} />

      <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)', mb: 1 }}>
        Pantalla de turnero
      </Typography>
      <Typography sx={{ color: '#b0b0b0', mb: 3, fontSize: 14 }}>
        Estos ítems aparecen en la sección "Información importante" de la pantalla TV de sala de espera.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
        {turneroItems.map((item, idx) => (
          <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography sx={{ color: 'var(--c-acento)', fontSize: 16, flexShrink: 0 }}>•</Typography>
            <TextField
              value={item}
              onChange={e => setTurneroItems(arr => arr.map((v, i) => i === idx ? e.target.value : v))}
              fullWidth
              size="small"
              inputProps={{ maxLength: 100, style: { color: '#e0e0e0' } }}
              sx={{
                '& .MuiInputBase-root': { color: '#e0e0e0' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' },
              }}
            />
            <IconButton
              size="small"
              onClick={() => setTurneroItems(arr => arr.filter((_, i) => i !== idx))}
              sx={{ color: '#ef9a9a', flexShrink: 0 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setTurneroItems(arr => [...arr, ''])}
          disabled={turneroItems.length >= 5}
          sx={{ color: 'var(--c-acento)', borderColor: 'var(--c-primario)', '&:hover': { borderColor: 'var(--c-acento)' } }}
        >
          Agregar ítem
        </Button>
        <Button
          variant="contained"
          onClick={guardarTurnero}
          disabled={guardandoTurnero}
          sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}
        >
          {guardandoTurnero ? 'Guardando...' : 'Guardar'}
        </Button>
        {exitoTurnero && (
          <Typography sx={{ fontSize: 13, color: exitoTurnero.startsWith('Error') ? '#ef9a9a' : '#81c784' }}>
            {exitoTurnero}
          </Typography>
        )}
      </Box>

      {/* ── Seguridad Turnero — PIN ─────────────────────────────────── */}
      <Divider sx={{ my: 5, borderColor: 'rgba(255,255,255,0.1)' }} />

      <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)', mb: 1 }}>
        Seguridad del Turnero
      </Typography>
      <Typography sx={{ color: '#b0b0b0', mb: 3, fontSize: 14 }}>
        El turnero (pantalla TV de sala de espera) requiere un PIN de 6 dígitos para activarse.
        El PIN se renueva automáticamente cada 15 días.
      </Typography>

      <Box sx={{ bgcolor: 'var(--c-superficie)', p: 2.5, borderRadius: 2, border: '1px solid var(--c-primario)', mb: 2 }}>
        {turneroPin ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ color: '#90a4ae', fontSize: 12, mb: 0.5 }}>PIN actual</Typography>
              <Typography sx={{ color: '#e0e0e0', fontWeight: 700, fontSize: 28, letterSpacing: 6, fontVariantNumeric: 'tabular-nums' }}>
                {turneroPin.pin}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ color: '#90a4ae', fontSize: 12, mb: 0.5 }}>Válido hasta</Typography>
              <Typography sx={{ color: '#b0b0b0', fontSize: 14 }}>
                {turneroPin.expira ? new Date(turneroPin.expira + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography sx={{ color: '#666', fontSize: 14 }}>Cargando...</Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          onClick={regenerarPin}
          disabled={regenerandoPin}
          sx={{ color: 'var(--c-acento)', borderColor: 'var(--c-primario)', '&:hover': { borderColor: 'var(--c-acento)' } }}
        >
          {regenerandoPin ? 'Regenerando...' : 'Regenerar PIN'}
        </Button>
        {exitoPin && (
          <Typography sx={{ fontSize: 13, color: exitoPin.startsWith('Error') ? '#ef9a9a' : '#81c784' }}>
            {exitoPin}
          </Typography>
        )}
      </Box>

      {/* ── Políticas Operativas ─────────────────────────────────────── */}
      <Divider sx={{ my: 5, borderColor: 'rgba(255,255,255,0.1)' }} />

      <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)', mb: 1 }}>
        Políticas Operativas
      </Typography>
      <Typography sx={{ color: '#b0b0b0', mb: 3, fontSize: 14 }}>
        Reglas propias de esta clínica sobre cómo trabaja el equipo — no son parte del sistema en sí,
        cada organización puede configurarlas distinto.
      </Typography>

      <Box sx={{ bgcolor: 'var(--c-superficie)', p: 2.5, borderRadius: 2, border: '1px solid var(--c-primario)', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Atención médica
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={!!politicas.evolucion_obligatoria}
              onChange={e => setPoliticas(p => ({ ...p, evolucion_obligatoria: e.target.checked }))}
            />
          }
          label={<Typography sx={{ fontSize: 14, color: '#e0e0e0' }}>Obligar evolución antes de finalizar un turno</Typography>}
          sx={{ m: 0 }}
        />

        {politicas.evolucion_obligatoria && (
          <TextField
            label="Cantidad mínima de caracteres"
            type="number"
            value={politicas.evolucion_min_caracteres}
            onChange={e => setPoliticas(p => ({ ...p, evolucion_min_caracteres: e.target.value }))}
            inputProps={{ min: 0, style: { color: '#e0e0e0' } }}
            InputLabelProps={{ style: { color: '#90a4ae' } }}
            sx={{ maxWidth: 260, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}
          />
        )}

        <FormControlLabel
          control={
            <Switch
              checked={!!politicas.permitir_borradores}
              onChange={e => setPoliticas(p => ({ ...p, permitir_borradores: e.target.checked }))}
            />
          }
          label={<Typography sx={{ fontSize: 14, color: '#e0e0e0' }}>Permitir guardar borradores de evolución</Typography>}
          sx={{ m: 0 }}
        />
      </Box>

      <Box sx={{ bgcolor: 'var(--c-superficie)', p: 2.5, borderRadius: 2, border: '1px solid var(--c-primario)', display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Confirmación de asistencia
        </Typography>
        <Typography sx={{ color: '#90a4ae', fontSize: 13 }}>
          Le pide al paciente que confirme o avise que no puede venir, unos días antes del turno, por email — sin cambiar el estado del turno. Solo se envía a pacientes con email registrado.
        </Typography>

        <FormControl sx={{ maxWidth: 260 }}>
          <InputLabel style={{ color: '#90a4ae' }}>Modo</InputLabel>
          <Select
            value={politicas.confirmacion_asistencia_modo}
            label="Modo"
            onChange={e => setPoliticas(p => ({ ...p, confirmacion_asistencia_modo: e.target.value }))}
            sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}
          >
            <MenuItem value="deshabilitada">Deshabilitada</MenuItem>
            <MenuItem value="manual">Manual — Recepción decide cuándo enviar</MenuItem>
            <MenuItem value="automatica">Automática</MenuItem>
          </Select>
        </FormControl>

        {politicas.confirmacion_asistencia_modo === 'automatica' && (
          <FormControl sx={{ maxWidth: 260 }}>
            <InputLabel style={{ color: '#90a4ae' }}>Enviar</InputLabel>
            <Select
              value={politicas.confirmacion_asistencia_horas_antes}
              label="Enviar"
              onChange={e => setPoliticas(p => ({ ...p, confirmacion_asistencia_horas_antes: e.target.value }))}
              sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}
            >
              <MenuItem value={24}>24 horas antes</MenuItem>
              <MenuItem value={48}>48 horas antes</MenuItem>
              <MenuItem value={72}>72 horas antes</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 2 }}>
        <Button
          variant="contained"
          onClick={guardarPoliticas}
          disabled={guardandoPoliticas}
          sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}
        >
          {guardandoPoliticas ? 'Guardando...' : 'Guardar políticas'}
        </Button>
        {exitoPoliticas && (
          <Typography sx={{ fontSize: 13, color: exitoPoliticas.startsWith('Error') ? '#ef9a9a' : '#81c784' }}>
            {exitoPoliticas}
          </Typography>
        )}
      </Box>

      {/* ── Zona de peligro ─────────────────────────────────────────── */}
      <Divider sx={{ my: 5, borderColor: 'rgba(255,255,255,0.1)' }} />

      <Box sx={{
        border: '1px solid #c62828', borderRadius: 2, p: 3,
        bgcolor: 'rgba(198,40,40,0.07)'
      }}>
        <Typography variant="h6" sx={{ color: '#ef9a9a', fontWeight: 700, mb: 0.5 }}>
          Zona de pruebas
        </Typography>
        <Typography sx={{ color: '#b0b0b0', fontSize: 13, mb: 2 }}>
          ⚠️ <strong style={{ color: '#ef9a9a' }}>Borrar este botón al finalizar pruebas.</strong>{' '}
          Elimina todos los pacientes, médicos, turnos e historias clínicas cargados durante las pruebas. Los usuarios admin no se eliminan.
        </Typography>

        {resultadoReset === 'ok' && (
          <Alert severity="success" sx={{ mb: 2 }}>Datos de prueba eliminados correctamente.</Alert>
        )}
        {resultadoReset.startsWith('error') && (
          <Alert severity="error" sx={{ mb: 2 }}>{resultadoReset}</Alert>
        )}

        <Button
          variant="outlined"
          onClick={() => { setDialogReset(true); setConfirmTexto(''); setResultadoReset(''); }}
          sx={{ color: '#ef9a9a', borderColor: '#c62828', '&:hover': { bgcolor: 'rgba(198,40,40,0.15)' } }}
        >
          Resetear datos de prueba
        </Button>
      </Box>

      {/* Dialog de confirmación */}
      <Dialog open={dialogReset} onClose={() => setDialogReset(false)}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', border: '1px solid #c62828', borderRadius: 2 } }}>
        <DialogTitle sx={{ color: '#ef9a9a', fontWeight: 700 }}>⚠️ Confirmar reseteo</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#e0e0e0', mb: 2, fontSize: 14 }}>
            Esta acción va a eliminar <strong>permanentemente</strong>:
          </Typography>
          <Box component="ul" sx={{ color: '#b0b0b0', fontSize: 13, pl: 2, mb: 2 }}>
            <li>Todos los pacientes</li>
            <li>Todos los médicos</li>
            <li>Todos los turnos</li>
            <li>Todas las historias clínicas y evoluciones</li>
            <li>Todo el staff (excepto administradores)</li>
          </Box>
          <Typography sx={{ color: '#e0e0e0', fontSize: 14, mb: 1.5 }}>
            Escribí <strong style={{ color: '#ef9a9a' }}>CONFIRMAR</strong> para continuar:
          </Typography>
          <TextField
            fullWidth
            value={confirmTexto}
            onChange={e => setConfirmTexto(e.target.value)}
            placeholder="CONFIRMAR"
            inputProps={{ style: { color: '#e0e0e0', letterSpacing: 2 } }}
            sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c62828' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogReset(false)} sx={{ color: '#b0b0b0' }}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={confirmTexto !== 'CONFIRMAR' || reseteando}
            onClick={handleResetear}
            sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}
          >
            {reseteando ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Borrar todo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
